import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { RichDataTable } from "../../../../components/table/RichDataTable";
import { Column } from "primereact/column";
import {
  PageHeight40,
  PageTitle,
  createSortByAttr,
  scrollToTop,
  toUIDate,
} from "../../../../components/Utils";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import ADConnectionTaskForm from "../../components/itConnections/ADConnectionTaskForm";
import ADConnectionTaskMenu from "../../components/itConnections/ADConnectionTaskMenu";

import { connect } from "react-redux";
import {
  setSelectedADConnections,
  exportADConnections,
  filterADConnections,
  closeConnectionTask,
  deleteConnectionTask,
  closeConnectionTasks,
} from "../../actions";
import {
  columnStyle,
  filterTasksByTaskType,
  isDeletionTask,
  renderNgumUserIDColumn,
  renderRequesterColumn,
  renderUserNameColumn,
  actionColumnStyle
} from "../../components/Utils";

const CONNECTION_TYPE = "AD";

const mapStateToProps = (state) => {
  return {
    isLoading: state.task.loading,
    data: state.task.adConnections,
    selection: state.task.selectedADConnections,
    connectionTaskType: state.task.currentADConnectionType,
  };
};
const mapDispatchToProps = (dispatch) => ({
  setSelection: (...args) => dispatch(setSelectedADConnections(...args)),
  exportADConnections: (...args) => dispatch(exportADConnections(...args)),
  filterConnections: (...args) => dispatch(filterADConnections(...args)),
  closeTask: (...args) => dispatch(closeConnectionTask(...args)),
  deleteTask: (...args) => dispatch(deleteConnectionTask(...args)),
  closeTasks: (...args) => dispatch(closeConnectionTasks(...args)),
});

const ADConnectionTaskList = ({
  className = "",
  isLoading,
  data,
  selection,
  setSelection,
  connectionTaskType,
  exportADConnections,
  filterConnections,
  closeTask,
  deleteTask,
  closeTasks,
  rows = 10,
}) => {
  const formRef = useRef(null);

  const onSave = useCallback((onHide) => {
    const task = formRef.current && formRef.current.getModel();
    if (!task) return;
    console.log(`>> saving ${CONNECTION_TYPE} connection task...`, task);
    closeTask({ connectionType: CONNECTION_TYPE, task }, onHide);
  }, [closeTask]);

  const onDelete = useCallback((task) => {
    console.log(`${CONNECTION_TYPE}ConnectionTaskList.onDelete()`, task);
    deleteTask({ connectionType: CONNECTION_TYPE, task });
  }, [deleteTask]);

  const onSaveTasks = useCallback((tasks, onHide) => {
    console.log(`>> saving ${CONNECTION_TYPE} connection tasks...`, tasks);
    closeTasks({ connectionType: CONNECTION_TYPE, tasks }, onHide);
  }, [closeTasks]);

  const isDeleting = isDeletionTask(connectionTaskType);

  const renderItemTable = useCallback((items, actionTemplate) => {
    const taskItems = items
      .map((item) => {
        if (!item.user) {
          console.log(
            "ADConnectionTaskList.renderItemTable() item.user is empty",
            item,
          );
        }
        return {
          ...item,
          user: (item.user || "").split(",").join(" "),
          requester: `${item.requester || ""}`.split(",").join(" "),
          requestDateUI: toUIDate(
            item.request_date,
            "YYYY-MM-DDTHH:mm:ss:SSSZ",
          ),
          requestDate: toUIDate(
            item.request_date,
            "YYYY-MM-DDTHH:mm:ss:SSSZ",
            "YYYY.MM.DD",
          ),
          userInCharge: `${item.userInCharge || ""}`.split(",").join(" "),
        };
      })
      .sort(createSortByAttr("request_date", true, true));

    return (
      <RichDataTable
        paginator
        paginatorLeftProps={{
          emptyText: "Keine Vodafone Accounts",
          itemName: "Vodafone Account",
          itemNamePlural: "Vodafone Accounts"
        }}
        data={taskItems}
        rows={rows}
        selection={selection}
        setSelection={setSelection}
        removableSort
        sortMode="multiple"
        isLoading={isLoading}
        connectionTaskType={connectionTaskType}
      >
        <Column selectionMode="multiple" style={{ width: "3em" }} />
        <Column
          field="parentCompanyName"
          header="Vertragspartner"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle }}
        />
        <Column
          field="user"
          header="Mitarbeiter"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle }}
          body={renderUserNameColumn}
        />
        <Column
          field="ngumUserId"
          header="nGUM UserID"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle }}
          body={renderNgumUserIDColumn}
        />
        <Column
          field="requester"
          header="Beantrager"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "12em" }}
          body={renderRequesterColumn}
        />
        <Column
          field="requestDate"
          header="Erstelldatum"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "10em", textAlign: "center" }}
          body={(item) => item.requestDateUI}
        />
        <Column
          field="status"
          header="Status"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "9em" }}
        />
        <Column
          body={actionTemplate}
          style={{ ...actionColumnStyle, textAlign: "center", width: "7em" }}
        />
      </RichDataTable>
    );
  }, [rows, selection, setSelection, isLoading, connectionTaskType]);

  const renderItemForm = useCallback((item, onHide) => {
    const connection = item;
    return (
      <div>
        <ADConnectionTaskForm
          ref={formRef}
          model={connection}
          isDeleting={isDeleting}
        />
        <FormMenu
          className="margin-big-top w3-border-top pad-big"
          style={{
            marginLeft: "-1em",
            marginRight: "-1em",
            marginBottom: "-1em",
          }}
          onSave={() => onSave(onHide)}
          onCancel={onHide}
          saveBtnName="saveADConnectionBtn"
          saveBtnLabel={isDeleting ? "Bestätigen" : undefined}
        />
      </div>
    );
  }, [isDeleting, onSave]);

  const getItemFormTitle = useCallback(() => {
    let title = "Vodafone Account bearbeiten";
    if (isDeleting) {
      title = "Vodafone Accountslöschung bestätigen";
    }
    return <h3 className="no-margin">{title}</h3>;
  }, [isDeleting]);

  const title = (
    <PageTitle>
      <ADConnectionTaskMenu
        className="w3-right"
        selectedADConnections={selection}
        exportADConnections={exportADConnections}
        closeADConnectionTasks={onSaveTasks}
        filterConnections={filterConnections}
        connectionTaskType={connectionTaskType}
        items={data}
      />
      <h3 className="no-margin">{"Vodafone Accounts"}</h3>
    </PageTitle>
  );

  return (
    <PageHeight40 className={className}>
      <ItemListManager
        key={Math.random()}
        title={title}
        addBtnHidden
        items={data.filter((item) =>
          filterTasksByTaskType(item, connectionTaskType),
        )}
        getItemFormTitle={getItemFormTitle}
        renderItemTable={renderItemTable}
        renderItemForm={renderItemForm}
        namePrefix={`${CONNECTION_TYPE}Connection`}
        itemDeleteable
        deleteTitle={`Vodafone Accountaufgabe löschen`}
        getDeleteText={(item) =>
          `Wollen Sie diese Aufgabe unwiderruflich löschen?`
        }
        onDelete={onDelete}
      />
    </PageHeight40>
  );
};

ADConnectionTaskList.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  selection: PropTypes.array,
  connectionTaskType: PropTypes.string,
  exportADConnections: PropTypes.func.isRequired,
  filterConnections: PropTypes.func.isRequired,
  closeTasks: PropTypes.func.isRequired,
  closeTask: PropTypes.func.isRequired,
  rows: PropTypes.number,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ADConnectionTaskList);
